export interface NodeSnapshot {
  metadata?: { name?: string; labels?: Record<string, string> };
  spec?: { unschedulable?: boolean };
  status?: { conditions?: { type: string; status: string }[]; allocatable?: { cpu?: unknown; memory?: unknown } };
}
export interface Distribution { label: string; count: number }

/** Strict Kubernetes quantity parsing; absent or malformed values remain unavailable, including suffix garbage. */
export function nodeQuantity(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const match = /^\+?((?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?)(Ki|Mi|Gi|Ti|Pi|Ei|n|u|m|k|K|M|G|T|P|E)?$/.exec(value);

  if (!match) {
    return null;
  }
  const suffix = match[2] || '';

  if (suffix && /[eE][+-]?\d/.test(match[1])) {
    return null;
  }
  const powers: Record<string, number> = {
    n: 1e-9, u: 1e-6, m: 1e-3, k: 1e3, K: 1e3, M: 1e6, G: 1e9, T: 1e12, P: 1e15, E: 1e18, Ki: 2 ** 10, Mi: 2 ** 20, Gi: 2 ** 30, Ti: 2 ** 40, Pi: 2 ** 50, Ei: 2 ** 60
  };
  const result = Number(match[1]) * (suffix ? powers[suffix] : 1);

  return Number.isFinite(result) && result >= 0 ? result : null;
}

function distribution(values: string[]): Distribution[] {
  const counts = new Map<string, number>();

  values.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));

  return Array.from(counts.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function nodeOverview(nodes: NodeSnapshot[]) {
  let ready = 0; let notReady = 0; let unknown = 0; let cordoned = 0;
  let cpu = 0; let memory = 0; let cpuReported = 0; let memoryReported = 0;
  const roles: string[] = []; const zones: string[] = [];

  nodes.forEach((node) => {
    const condition = node.status?.conditions?.find((item) => item.type === 'Ready');

    if (condition?.status === 'True') {
      ready++;
    } else if (condition?.status === 'False') {
      notReady++;
    } else {
      unknown++;
    }
    if (node.spec?.unschedulable === true) {
      cordoned++;
    }
    const nodeCPU = nodeQuantity(node.status?.allocatable?.cpu); const nodeMemory = nodeQuantity(node.status?.allocatable?.memory);

    if (nodeCPU !== null) {
      cpu += nodeCPU; cpuReported++;
    }
    if (nodeMemory !== null) {
      memory += nodeMemory; memoryReported++;
    }
    const labels = node.metadata?.labels || {};
    const nodeRoles = Object.keys(labels).filter((key) => key.startsWith('node-role.kubernetes.io/') && key.length > 'node-role.kubernetes.io/'.length).map((key) => {
      const role = key.slice('node-role.kubernetes.io/'.length);

      return ['control-plane', 'master'].includes(role) ? 'Control plane' : role === 'worker' ? 'Worker' : role;
    });

    roles.push(...(nodeRoles.length ? Array.from(new Set(nodeRoles)) : ['Role unspecified']));
    const zone = labels['topology.kubernetes.io/zone'] || labels['failure-domain.beta.kubernetes.io/zone'];

    if (zone) {
      zones.push(zone);
    }
  });

  return {
    total: nodes.length, ready, notReady, unknown, cordoned, cpu, memory, cpuReported, memoryReported, roles: distribution(roles), zones: distribution(zones), zoneReported: zones.length
  };
}

const POOL_LABELS = ['karpenter.sh/nodepool', 'eks.amazonaws.com/nodegroup', 'cloud.google.com/gke-nodepool', 'kubernetes.azure.com/agentpool', 'agentpool', 'nodepool', 'pool'];

export function nodePoolLabels(nodes: NodeSnapshot[]): string[] {
  const keys = Array.from(new Set(nodes.flatMap((node) => Object.keys(node.metadata?.labels || {}).filter((key) => !key.startsWith('node-role.kubernetes.io/') && /pool|nodegroup|node-group|machine-deployment/i.test(key)))));

  return keys.sort((a, b) => {
    const ai = POOL_LABELS.indexOf(a); const bi = POOL_LABELS.indexOf(b);

    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi) || a.localeCompare(b);
  });
}
export function nodePools(nodes: NodeSnapshot[], labelKey: string) {
  const pools = new Map<string, NodeSnapshot[]>();

  nodes.forEach((node) => {
    const value = labelKey ? node.metadata?.labels?.[labelKey] : undefined;
    const key = value === undefined || value === '' ? 'missing:' : `value:${ value }`;
    const entries = pools.get(key) || [];

    entries.push(node); pools.set(key, entries);
  });

  return Array.from(pools.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([key, entries]) => ({
    key, label: key === 'missing:' ? 'Unassigned' : key.slice('value:'.length), missing: key === 'missing:', nodes: entries, overview: nodeOverview(entries)
  }));
}
